import * as Sentry from '@sentry/react';

import { LoadError } from '@/components/ui/load-error';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { PlotSection } from '@/warranty-v3/components/plot-section';
import { Card, CardContent } from '@/warranty-v3/components/ui/card';
import { useCurrentSystemId } from '@/warranty-v3/hooks/use-current-system-id';
import { useSelectedFilters } from '@/warranty-v3/hooks/use-selected-filters';
import { GetDataQuery } from '@/warranty-v3/queries/get-data';

import { ConditionViolations } from './condition-violations';
import { DataLoading } from './data-loading';
import { Overview } from './overview';
import { WarrantyPenalties } from './warranty-penalties';

function WarrantyNotConfiguredMessage({
  sectionName,
  message,
}: {
  sectionName: string;
  message: string;
}) {
  return (
    <PlotSection sectionName={sectionName}>
      <Card className="w-full">
        <CardContent>
          <p className="py-6 text-center text-gray-500">{message}</p>
        </CardContent>
      </Card>
    </PlotSection>
  );
}

export function Data() {
  const systemId = useCurrentSystemId();
  const [selectedFilters] = useSelectedFilters();
  const { asset } = useCurrentAsset();
  const me = useMe();
  const dataQuery = GetDataQuery.useQuery({ systemId, selectedFilters });

  const assetId = asset?.id ?? '';
  const penaltiesNotConfigured =
    me.config.warrantyAssetIdsWithoutPenalties?.includes(assetId) ?? false;
  const conditionsNotConfigured =
    me.config.warrantyAssetIdsWithoutConditions?.includes(assetId) ?? false;

  if (dataQuery.isLoading) {
    return <DataLoading />;
  }

  if (dataQuery.isError || !dataQuery.data) {
    Sentry.captureException(dataQuery.error);
    return <LoadError title="An error occurred while loading warranty data" />;
  }

  return (
    <>
      <Overview unitOverview={dataQuery.data.unitOverview} />
      {penaltiesNotConfigured ? (
        <WarrantyNotConfiguredMessage
          sectionName="Penalty Overview"
          message="Penalties are not defined for this site's warranty."
        />
      ) : (
        <WarrantyPenalties
          warrantyPenalties={dataQuery.data.warrantyPenalties}
        />
      )}
      {conditionsNotConfigured ? (
        <WarrantyNotConfiguredMessage
          sectionName="Condition Violations"
          message="Conditions are not defined for this site's warranty."
        />
      ) : (
        <ConditionViolations
          conditionViolations={dataQuery.data.conditionViolations}
        />
      )}
    </>
  );
}
